import React, { useState } from 'react'
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import LoadingButton from '@mui/lab/LoadingButton'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { TablePaginationActions } from '~/src/components/organisms'
import { wishListRepository } from '~/src/repositories'
import { useMessage, useCurrency } from '~/src/hooks'
import { formatMoney } from '~/src/utils/formatMoney'
import { stringToNumber } from '~/src/libs'
import {
  IWishListParams,
  IWishListRow,
  IWishListResponse,
  WISH_LIST_DEFAULT_LIMIT,
} from '~/src/repositories/WishList/models/WishList'
import { convertWishListCatalystText, convertWishListStatusText } from '~/src/utils/convert'

interface IWishListTableProps {
  data?: IWishListRow[]
  pagination?: IWishListResponse['pagination']
  onNextPages: (_params: IWishListParams) => void
  params: IWishListParams
}

interface IColumn {
  id:
    | 'companyName'
    | 'refNo'
    | 'productName'
    | 'makerName'
    | 'carName'
    | 'carModel'
    | 'engineModel'
    | 'year'
    | 'wishPrice'
    | 'stockType'
    | 'stockSource'
    | 'orderDate'
    | 'status'
  label: string
  minWidth: number
  align?: 'right'
  render: (row: IWishListRow) => React.ReactNode
}

const formatOrderDate = (orderedAt?: number | null): string => {
  if (!orderedAt) {
    return '-'
  }
  return moment.unix(orderedAt).format('YYYY-MM-DD')
}

const formatWishPriceDisplay = (
  wishPrice: string | null | undefined,
  baseCurrencyUnit?: string,
  unitPosition?: string,
): string => {
  if (!wishPrice) {
    return '-'
  }

  const formatted = formatMoney(
    stringToNumber(wishPrice),
    baseCurrencyUnit,
    unitPosition as 'left' | 'right' | undefined,
  )

  return formatted || '-'
}

export const WishListTable: React.FC<IWishListTableProps> = ({
  data,
  pagination,
  onNextPages,
  params,
}): JSX.Element => {
  const { t } = useTranslation()
  const { handleOpenWithErrorMessage } = useMessage()
  const { baseCurrencyUnit, unitPosition } = useCurrency()
  const [isExportExcelLoading, setIsExportExcelLoading] = useState(false)

  const rowsPerPage = pagination?.limitValue ?? WISH_LIST_DEFAULT_LIMIT
  const page =
    params?.offset != null ? params.offset - 1 : (pagination?.currentPage ?? 1) - 1

  const handleChangePage = (_event: unknown, newPage: number) => {
    onNextPages({ ...(params ?? {}), limit: rowsPerPage, offset: newPage + 1 })
  }

  const handleExportExcel = async (exportParams: IWishListParams) => {
    setIsExportExcelLoading(true)
    try {
      const { data } = await wishListRepository.exportExcel(exportParams)
      if (data) {
        const url = URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'wish-list.xlsx')
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
      }
    } catch (e: unknown) {
      console.error(e)
      const message = e instanceof Error ? e.message : String(e)
      handleOpenWithErrorMessage(message)
    } finally {
      setIsExportExcelLoading(false)
    }
  }

  const columns: IColumn[] = [
    {
      id: 'companyName',
      label: t('wish_list.company_name'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.companyName}</Typography>,
    },
    {
      id: 'refNo',
      label: t('wish_list.original_ref_no'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.refNo}</Typography>,
    },
    {
      id: 'productName',
      label: t('wish_list.product_name'),
      minWidth: 400,
      render: (row) => {
        const catalystLabel = convertWishListCatalystText(row.stockType, row.withCatalyst)

        return (
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12 }}>
              {row.productName}
              {catalystLabel ? ` (${catalystLabel})` : ''}
            </Typography>
            <Typography variant="body2">{row.displayName}</Typography>
          </Stack>
        )
      },
    },
    {
      id: 'makerName',
      label: t('wish_list.maker_name'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.makerName}</Typography>,
    },
    {
      id: 'carName',
      label: t('wish_list.car_name'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.carName}</Typography>,
    },
    {
      id: 'carModel',
      label: t('wish_list.car_model'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.carModel}</Typography>,
    },
    {
      id: 'engineModel',
      label: t('wish_list.engine_model'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.engineModel}</Typography>,
    },
    {
      id: 'year',
      label: t('wish_list.year'),
      minWidth: 120,
      render: (row) => <Typography variant="body2">{row.year ?? '-'}</Typography>,
    },
    {
      id: 'wishPrice',
      label: t('wish_list.wish_price'),
      minWidth: 150,
      align: 'right',
      render: (row) => (
        <Typography variant="body2">
          {formatWishPriceDisplay(row.wishPrice, baseCurrencyUnit, unitPosition)}
        </Typography>
      ),
    },
    {
      id: 'stockType',
      label: t('wish_list.product_type'),
      minWidth: 150,
      render: (row) => <Typography variant="body2">{row.stockType}</Typography>,
    },
    {
      id: 'stockSource',
      label: t('wish_list.stock_source'),
      minWidth: 120,
      render: (row) => <Typography variant="body2">{row.stockSource || '-'}</Typography>,
    },
    {
      id: 'orderDate',
      label: t('wish_list.order_date'),
      minWidth: 150,
      render: (row) => (
        <Typography variant="body2">{formatOrderDate(row.orderedAt)}</Typography>
      ),
    },
    {
      id: 'status',
      label: t('wish_list.status'),
      minWidth: 160,
      render: (row) => (
        <Chip
          label={convertWishListStatusText(row.status, row.isAvailable)}
          variant="outlined"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            height: '24px',
            color:
              row.status === 'OrderConfirmed'
                ? '#1976d2'
                : row.status === 'Available' || row.isAvailable
                  ? '#2e7d32'
                  : '#ed6c02',
          }}
        />
      ),
    },
  ]

  return (
    <Paper sx={{ width: '100%', maxWidth: '100%', mb: 2, boxSizing: 'border-box' }}>
      <Stack direction="row" spacing={1} padding="20px">
        <LoadingButton
          type="submit"
          loading={isExportExcelLoading}
          startIcon={<></>}
          loadingPosition="start"
          size="medium"
          variant="outlined"
          sx={{
            minWidth: isExportExcelLoading ? '230px' : '200px',
            height: '40px',
            padding: '7px 22px 7px 19px',
            background: '#FFFFFF',
          }}
          onClick={() => handleExportExcel(params ?? {})}
        >
          <DownloadIcon sx={{ width: '22px', height: '25px', marginRight: '8px' }} />
          {t('wish_list.excel_export')}
        </LoadingButton>
      </Stack>
      <TableContainer>
        <Table stickyHeader aria-label="wish list table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: '24px',
                        fontStyle: 'normal',
                        fontWeight: 'medium',
                      }}
                    >
                      {column.label}
                    </Typography>
                    <Typography
                      sx={{
                        height: '14px',
                        width: '2px',
                        backgroundColor: '#E0E0E0',
                      }}
                    />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row) => (
              <TableRow hover tabIndex={-1} key={row.id}>
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align} sx={{ padding: '13px' }}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={pagination?.totalCount ?? 0}
        rowsPerPage={rowsPerPage}
        sx={{
          fontSize: '12px',
          fontStyle: 'normal',
          lineHeight: '20px',
          fontWeight: 'Regular',
        }}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationActions}
      />
    </Paper>
  )
}
